import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Trash2, CalendarIcon, MessageSquare, CheckCircle2, Lock, Unlock } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import TaskDetailSheet from '../components/TaskDetailSheet';
import SlackChannelPicker from '../components/SlackChannelPicker';

const STATUS_OPTIONS = [
  { value: 'to_be_done', label: 'To Be Done' },
  { value: 'doing', label: 'Doing' },
  { value: 'need_clarification', label: 'Need Clarification' },
  { value: 'done', label: 'Done' },
  { value: 'locked', label: 'Locked' },
];

const TRANSITION_STATUSES = [
  { value: 'to_be_done', label: 'To Be Done' },
  { value: 'doing', label: 'Doing' },
  { value: 'need_clarification', label: 'Need Clarification' },
];

const statusColors = {
  to_be_done: { bg: '#E2E8F0', text: '#34495e' },
  doing: { bg: '#f39c12', text: '#FFFFFF' },
  need_clarification: { bg: '#e74c3c', text: '#FFFFFF' },
  done: { bg: '#27ae60', text: '#FFFFFF' },
  locked: { bg: '#BDC3C7', text: '#FFFFFF' },
};

const isOverdue = (task) => {
  if (task.status === 'done' || !task.estimated_completion) return false;
  return new Date() > new Date(task.estimated_completion);
};

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [confirmDoneTaskId, setConfirmDoneTaskId] = useState(null);
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('none');
  const [assignedTo, setAssignedTo] = useState('none');
  const [status, setStatus] = useState('to_be_done');
  const [estDate, setEstDate] = useState(undefined);
  const [estTime, setEstTime] = useState('12:00');
  const [slackChannelId, setSlackChannelId] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, catsRes] = await Promise.all([
        api.get('/tasks'), api.get('/users'), api.get('/categories'),
      ]);
      setTasks(tasksRes.data.tasks);
      setUsers(usersRes.data.users);
      setCategories(catsRes.data.categories);
    } catch (err) { console.error('Failed to fetch:', err); }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setCategoryId('none'); setAssignedTo('none');
    setStatus('to_be_done'); setEstDate(undefined); setEstTime('12:00'); setEditingTask(null);
    setSlackChannelId('');
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (task) => {
    setEditingTask(task);
    setTitle(task.title);
    setDescription(task.description || '');
    setCategoryId(task.category_id || 'none');
    setAssignedTo(task.assigned_to || 'none');
    setStatus(task.status);
    setSlackChannelId(task.slack_channel_id || '');
    if (task.estimated_completion) {
      const d = new Date(task.estimated_completion);
      setEstDate(d);
      setEstTime(format(d, 'HH:mm'));
    } else {
      setEstDate(undefined);
      setEstTime('12:00');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    let estimatedCompletion = '';
    if (estDate) {
      const [hours, minutes] = estTime.split(':').map(Number);
      const d = new Date(estDate);
      d.setHours(hours || 0, minutes || 0, 0, 0);
      estimatedCompletion = d.toISOString();
    }
    const payload = {
      title: title.trim(),
      description: description.trim(),
      category_id: categoryId === 'none' ? '' : categoryId,
      assigned_to: assignedTo === 'none' ? '' : assignedTo,
      status,
      estimated_completion: estimatedCompletion || null,
      slack_channel_id: slackChannelId.trim() || null,
    };
    try {
      if (editingTask) {
        await api.put(`/tasks/${editingTask.task_id}`, payload);
        toast.success('Task updated');
      } else {
        await api.post('/tasks', payload);
        toast.success('Task created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save task'); }
  };

  const handleDelete = (taskId) => {
    setConfirmDeleteTaskId(taskId);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteTaskId) return;
    try {
      await api.delete(`/tasks/${confirmDeleteTaskId}`);
      toast.success('Task deleted'); fetchData(); setSelectedTaskIds(prev => prev.filter(id => id !== confirmDeleteTaskId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete task'); }
    finally { setConfirmDeleteTaskId(null); }
  };

  const toggleSelectTask = (taskId) => {
    setSelectedTaskIds(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  };

  const toggleSelectAll = (taskList) => {
    const allIds = taskList.map(t => t.task_id);
    const allSelected = allIds.every(id => selectedTaskIds.includes(id));
    if (allSelected) {
      setSelectedTaskIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedTaskIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedTaskIds.length === 0) return;
    try {
      await api.post('/tasks/bulk-delete', { task_ids: selectedTaskIds });
      toast.success(`${selectedTaskIds.length} tasks deleted`);
      setSelectedTaskIds([]); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete tasks'); }
    finally { setShowBulkDeleteConfirm(false); }
  };

  const handleUnlock = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/unlock`);
      toast.success('Task unlocked'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unlock'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Status updated'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleMarkDone = (taskId) => {
    setConfirmDoneTaskId(taskId);
  };

  const confirmMarkDone = async () => {
    if (!confirmDoneTaskId) return;
    try {
      await api.put(`/tasks/${confirmDoneTaskId}`, { status: 'done' });
      toast.success('Task marked as Done'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
    finally { setConfirmDoneTaskId(null); }
  };

  const getUserName = (task) => {
    if (task?.assigned_to_removed_name) return task.assigned_to_removed_name;
    if (!task?.assigned_to) return 'Unassigned';
    return users.find(u => u.user_id === task.assigned_to)?.name || 'Unassigned';
  };
  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.name || '-';
  const canModifyTask = (task) => user?.role === 'admin' || task.assigned_to === user?.user_id;
  const selectedTask = selectedTaskId ? tasks.find(t => t.task_id === selectedTaskId) || null : null;

  const filteredTasks = tasks.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterCategory !== 'all' && t.category_id !== filterCategory) return false;
    return true;
  });

  // All Tasks view: float the current user's tasks to the top, keep others below.
  const sortedFilteredTasks = user?.user_id
    ? [
        ...filteredTasks.filter(t => t.assigned_to === user.user_id),
        ...filteredTasks.filter(t => t.assigned_to !== user.user_id),
      ]
    : filteredTasks;

  // My Tasks: assigned to current user, max 15 done tasks
  const myTasks = tasks.filter(t => t.assigned_to === user?.user_id);
  const myActiveTasks = myTasks.filter(t => t.status !== 'done');
  const myDoneTasks = myTasks.filter(t => t.status === 'done').slice(0, 15);
  const myFilteredTasks = [...myActiveTasks, ...myDoneTasks];

  const [activeView, setActiveView] = useState('all');

  const isAdmin = user?.role === 'admin';

  const renderTaskTable = (taskList, testId) => (
    <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full" data-testid={testId}>
          <thead>
            <tr className="bg-[#F8F9FA]">
              {isAdmin && (
                <th className="w-10 px-3 py-2.5">
                  <Checkbox
                    checked={taskList.length > 0 && taskList.every(t => selectedTaskIds.includes(t.task_id))}
                    onCheckedChange={() => toggleSelectAll(taskList)}
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Task</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Category</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Assigned To</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Status</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Est. Completion</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {taskList.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-[#BDC3C7] text-sm">No tasks found</td></tr>
            ) : (
              taskList.map((task, idx) => {
                const sc = statusColors[task.status] || statusColors.to_be_done;
                const overdue = isOverdue(task);
                const isLocked = task.status === 'locked';
                const isMine = user?.user_id && task.assigned_to === user.user_id;
                const prevMine = idx > 0 && user?.user_id && taskList[idx - 1].assigned_to === user.user_id;
                const showOthersDivider = idx > 0 && prevMine && !isMine;
                return (
                  <React.Fragment key={task.task_id}>
                    {showOthersDivider && (
                      <tr className="bg-[#F8F9FA]" data-testid="others-tasks-divider">
                        <td colSpan={isAdmin ? 7 : 6} className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#BDC3C7]">
                          Other teammates&apos; tasks
                        </td>
                      </tr>
                    )}
                    <tr className={`border-b border-[#E2E8F0] last:border-0 transition-colors ${isLocked ? 'opacity-50 bg-[#F8F9FA]' : selectedTaskIds.includes(task.task_id) ? 'bg-blue-50' : overdue ? 'bg-red-50 hover:bg-red-100/70' : 'hover:bg-[#F8F9FA]/50'}`} data-testid={isLocked ? `locked-task-${task.task_id}` : overdue ? `overdue-task-${task.task_id}` : undefined}>
                    {isAdmin && (
                      <td className="w-10 px-3 py-3">
                        <Checkbox
                          checked={selectedTaskIds.includes(task.task_id)}
                          onCheckedChange={() => toggleSelectTask(task.task_id)}
                          data-testid={`select-task-${task.task_id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedTaskId(task.task_id)} className="text-left group" data-testid={`open-task-${task.task_id}`}>
                        <p className={`text-sm group-hover:underline ${isLocked ? 'text-[#BDC3C7]' : overdue ? 'text-[#e74c3c] font-bold' : 'text-[#34495e] group-hover:text-[#2c3e50]'}`}>
                          {isLocked && <Lock size={12} className="inline mr-1 -mt-0.5 text-[#BDC3C7]" />}
                          {overdue && <span className="inline-block w-2 h-2 rounded-full bg-[#e74c3c] mr-1.5 -mt-0.5" />}
                          {task.title}
                        </p>
                        {isLocked && <p className="text-[10px] text-[#BDC3C7] italic mt-0.5">Waiting for previous task</p>}
                        {!isLocked && task.description && <p className="text-xs text-[#BDC3C7] mt-0.5 truncate max-w-[200px]">{task.description}</p>}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#7F8C8D]">{getCategoryName(task.category_id)}</td>
                    <td className="px-4 py-3 text-sm text-[#7F8C8D]">
                      {task.assigned_to_removed_name ? (
                        <span className="text-[#e74c3c] italic text-xs">{task.assigned_to_removed_name}</span>
                      ) : getUserName(task)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {isLocked ? (
                          <>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold bg-[#BDC3C7] text-white">
                              <Lock size={10} /> Locked
                            </span>
                            {isAdmin && (
                              <button onClick={() => handleUnlock(task.task_id)} className="p-1 rounded-sm text-[#f39c12] hover:bg-[#f39c12] hover:text-white transition-colors" title="Unlock task" data-testid={`unlock-task-${task.task_id}`}>
                                <Unlock size={14} />
                              </button>
                            )}
                          </>
                        ) : canModifyTask(task) && task.status !== 'done' ? (
                          <Select value={task.status} onValueChange={(v) => handleStatusChange(task.task_id, v)}>
                            <SelectTrigger className="h-auto w-auto border-0 p-0 shadow-none" data-testid={`task-status-${task.task_id}`}>
                              <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-bold" style={{ backgroundColor: sc.bg, color: sc.text }}>
                                {STATUS_OPTIONS.find(s => s.value === task.status)?.label}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {TRANSITION_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-bold" style={{ backgroundColor: sc.bg, color: sc.text }}>
                            {STATUS_OPTIONS.find(s => s.value === task.status)?.label}
                          </span>
                        )}
                        {canModifyTask(task) && task.status !== 'done' && !isLocked && (
                          <button
                            onClick={() => handleMarkDone(task.task_id)}
                            className="p-1 rounded-sm text-[#27ae60] hover:bg-[#27ae60] hover:text-white transition-colors"
                            title="Mark as Done"
                            data-testid={`mark-done-${task.task_id}`}
                          >
                            <CheckCircle2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#7F8C8D]">
                      {task.estimated_completion ? format(new Date(task.estimated_completion), 'MMM d, yyyy HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setSelectedTaskId(task.task_id)} className="p-1.5 text-[#7F8C8D] hover:text-[#34495e] transition-colors" data-testid={`comments-task-${task.task_id}`}>
                          <MessageSquare size={14} />
                        </button>
                        {canModifyTask(task) && (
                          <>
                            <button onClick={() => openEdit(task)} className="p-1.5 text-[#7F8C8D] hover:text-[#34495e] transition-colors" data-testid={`edit-task-${task.task_id}`}>
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => handleDelete(task.task_id)} className="p-1.5 text-[#7F8C8D] hover:text-[#e74c3c] transition-colors" data-testid={`delete-task-${task.task_id}`}>
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e]">All Tasks</h2>
          <p className="text-sm text-[#7F8C8D]">{tasks.length} total tasks</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && selectedTaskIds.length > 0 && (
            <Button onClick={() => setShowBulkDeleteConfirm(true)} variant="outline" className="border-[#e74c3c] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white rounded-sm" data-testid="bulk-delete-btn">
              <Trash2 size={16} className="mr-1" /> Delete {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''}
            </Button>
          )}
          <Button onClick={openCreate} className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="create-task-btn">
            <Plus size={16} className="mr-1" /> New Task
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex border-b border-[#E2E8F0] mb-4">
        <button
          onClick={() => setActiveView('my')}
          className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors ${
            activeView === 'my' ? 'border-[#34495e] text-[#34495e]' : 'border-transparent text-[#BDC3C7] hover:text-[#7F8C8D]'
          }`}
          data-testid="tab-my-tasks"
        >
          My Tasks ({myFilteredTasks.length})
        </button>
        <button
          onClick={() => setActiveView('all')}
          className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-colors ${
            activeView === 'all' ? 'border-[#34495e] text-[#34495e]' : 'border-transparent text-[#BDC3C7] hover:text-[#7F8C8D]'
          }`}
          data-testid="tab-all-tasks"
        >
          All Tasks ({filteredTasks.length})
        </button>
      </div>

      {activeView === 'all' && (
        <>
          {/* Filters */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px] rounded-sm border-[#E2E8F0]" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] rounded-sm border-[#E2E8F0]" data-testid="filter-category">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {renderTaskTable(sortedFilteredTasks, 'tasks-table')}
        </>
      )}

      {activeView === 'my' && (
        <>
          <p className="text-xs text-[#7F8C8D] mb-4">
            Showing your assigned tasks. Done tasks limited to last 15.
          </p>
          {renderTaskTable(myFilteredTasks, 'my-tasks-table')}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-md bg-white border border-[#E2E8F0] rounded-sm" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">
              {editingTask ? 'Edit Task' : 'New Task'}
            </DialogTitle>
            <DialogDescription className="sr-only">{editingTask ? 'Edit task details' : 'Create a new task'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="task-title-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Task description (optional)" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="task-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="task-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Assigned To</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="task-assignee-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Estimated Completion</Label>
              <div className="flex gap-2 mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-start text-left font-normal rounded-sm border-[#E2E8F0]" data-testid="task-date-picker">
                      <CalendarIcon size={14} className="mr-2" />
                      {estDate ? format(estDate, 'MMM d, yyyy') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={estDate} onSelect={setEstDate} fromDate={new Date()} toDate={addDays(new Date(), 14)} />
                  </PopoverContent>
                </Popover>
                <Input type="time" value={estTime} onChange={(e) => setEstTime(e.target.value)} className="w-28 rounded-sm border-[#E2E8F0]" data-testid="task-time-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Slack Channel (on completion)</Label>
              <div className="mt-1">
                <SlackChannelPicker
                  value={slackChannelId}
                  onChange={setSlackChannelId}
                  placeholder="Pick a Slack channel (optional)"
                  testId="task-slack-channel-picker"
                />
              </div>
              <p className="text-[10px] text-[#BDC3C7] mt-1">Posts &quot;{'{'}task{'}'} is completed by {'{'}assignee{'}'}&quot; here when marked Done. Overrides the global channel.</p>
            </div>
            <Button onClick={handleSubmit} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="task-submit-btn">
              {editingTask ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Detail Sheet with Comments/Activity */}
      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTaskId(null)}
        users={users}
        categories={categories}
        onTaskUpdated={() => fetchData()}
      />

      {/* Confirm Mark as Done */}
      <AlertDialog open={!!confirmDoneTaskId} onOpenChange={(open) => { if (!open) setConfirmDoneTaskId(null); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-done-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Mark task as Done?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              Are you sure you want to mark this task as Done? This will trigger any configured notifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-done-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkDone} className="rounded-sm bg-[#27ae60] hover:bg-[#219a52] text-white" data-testid="confirm-done-yes">Yes, mark as Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Delete */}
      <AlertDialog open={!!confirmDeleteTaskId} onOpenChange={(open) => { if (!open) setConfirmDeleteTaskId(null); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Delete this task?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              This action cannot be undone. The task and all its comments will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-[#e74c3c] hover:bg-[#c0392b] text-white" data-testid="confirm-delete-yes">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Bulk Delete */}
      <AlertDialog open={showBulkDeleteConfirm} onOpenChange={(open) => { if (!open) setShowBulkDeleteConfirm(false); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Delete {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              This action cannot be undone. All selected tasks and their comments will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="rounded-sm bg-[#e74c3c] hover:bg-[#c0392b] text-white" data-testid="confirm-bulk-delete-yes">Yes, delete all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
